import { useCallback, useEffect, useMemo } from 'react';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { ShopCategoriesGrid } from '~/components/ui/Shop';
import {
	ShopCategory,
	useShopCategoryControls,
} from '~/components/ui/ShopCategory';
import { rage, useObservable } from '~/rage';
import { VehicleTuningCategory } from '~/shared';
import { VEHICLE_TUNING_SHOP_CATEGORIES } from '~/layouts/tuningStudio/tuningGroups.model';
import {
	setActiveCategory,
	setActiveStage,
	TuningSlice,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { getCategoryImageByType } from './CategoryTab.model';

const CategoryTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const activeCategory = useLayoutSelector(
		(state) => state.tuning.activeCategory,
	);
	const shopCategories = VEHICLE_TUNING_SHOP_CATEGORIES;
	const modalState = useObservable(rage.modal.getCurrentModal());
	const shopItems = useLayoutSelector((state) => state.tuning.shopItems);
	const wheelSettingsRanges = useObservable(
		rage.vehicleTuningShop.getWheelsSettingsRanges(),
	);
	const isUsingStockWheels = useMemo(
		() =>
			shopItems.some(
				(e) =>
					e.id === `stock_${VehicleTuningCategory.WHEEL_SETTINGS}` &&
					e.isInstalled,
			),
		[shopItems],
	);

	const filteredShopCategories = useMemo(() => {
		if (shopCategories.length === 0) {
			return [];
		}

		return shopCategories.filter((category) => {
			if (category.id === VehicleTuningCategory.WHEEL_SETTINGS) {
				if (!wheelSettingsRanges || isUsingStockWheels) {
					return false;
				}
			} else {
				const categoryItems = shopItems.filter(
					(e) => e.categoryId === category.id,
				);
				const nonStockItems = categoryItems.filter(
					(e) => !e.id.startsWith('stock_'),
				);
				if (nonStockItems.length === 0) {
					return false;
				}
			}

			return true;
		});
	}, [shopCategories, wheelSettingsRanges, isUsingStockWheels, shopItems]);

	const handleSelect = useCallback(
		(category: VehicleTuningCategory) => {
			let nextActiveStage: TuningSlice['activeStage'] = 'list';
			if (category === VehicleTuningCategory.WHEEL_SETTINGS) {
				nextActiveStage = 'wheel_settings';
				const seenTrackerKey = `${VehicleTuningCategory.WHEEL_SETTINGS}`;
				rage.vehicleTuningShop.markSeen(seenTrackerKey);
			}

			dispatch(setActiveStage(nextActiveStage));
			dispatch(setActiveCategory(category));
		},
		[shopCategories],
	);

	const categoryUnseenMap = useMemo(() => {
		const map: Record<VehicleTuningCategory, boolean> = {} as Record<
			VehicleTuningCategory,
			boolean
		>;

		for (const category of filteredShopCategories) {
			if (category.id === VehicleTuningCategory.WHEEL_SETTINGS) {
				const seenTrackerKey = `${VehicleTuningCategory.WHEEL_SETTINGS}`;
				map[category.id] = rage.vehicleTuningShop.isUnseen(seenTrackerKey);
				continue;
			}

			const categoryItems = shopItems.filter(
				(item) =>
					item.categoryId === category.id && !item.id.startsWith('stock_'),
			);

			for (const item of categoryItems) {
				const seenTrackerKey = `${category.id}.${item.id}`;
				if (rage.vehicleTuningShop.isUnseen(seenTrackerKey)) {
					map[category.id] = true;
					break;
				}
			}
		}

		return map;
	}, [filteredShopCategories, shopItems]);

	const { setMaxCategoryIndex, activeCategoryIndex, setActiveCategoryIndex } =
		useShopCategoryControls(
			activeCategory
				? filteredShopCategories.map((item) => item.id).indexOf(activeCategory)
				: null,
			(index) => handleSelect(filteredShopCategories[index].id),
			!!modalState,
		);

	useEffect(() => {
		if (filteredShopCategories.length > 0) {
			setMaxCategoryIndex(filteredShopCategories.length);
		}
	}, [filteredShopCategories]);

	if (filteredShopCategories.length === 0) {
		return null;
	}

	return (
		<ScrollContainer height={687} style={{ marginTop: 30 }} showMask>
			<ShopCategoriesGrid>
				{filteredShopCategories.map(({ id, name }, index) => (
					<ShopCategory
						name={name}
						onClick={() => handleSelect(id)}
						icon={getCategoryImageByType(id)}
						isActive={activeCategoryIndex === index}
						isNew={categoryUnseenMap[id] ?? false}
						key={`category#${id}`}
						onMouseEnter={() => setActiveCategoryIndex(index)}
						onMouseLeave={() => setActiveCategoryIndex(null)}
					/>
				))}
			</ShopCategoriesGrid>
		</ScrollContainer>
	);
};

export { CategoryTab };
